import { useRef, ChangeEvent, KeyboardEvent } from 'react';
import { TextArea } from '@teachstonellc/component-library';

const BULLET = '• ';

function enforceBullets(text: string): string {
  return text
    .split('\n')
    .map(line =>
      line === '' || line.startsWith(BULLET) ? line : BULLET + line
    )
    .join('\n');
}

interface BulletedTextAreaProps {
  label: string;
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
}

export function BulletedTextArea({
  label,
  placeholder,
  value,
  onChange
}: BulletedTextAreaProps) {
  const ref = useRef<HTMLTextAreaElement>(null);

  function handleChange(e: ChangeEvent<HTMLTextAreaElement>) {
    const raw = e.currentTarget.value;
    const enforced = enforceBullets(raw);
    onChange(enforced);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    const textarea = e.currentTarget;
    const { selectionStart, selectionEnd } = textarea;

    if (e.key === 'Enter') {
      e.preventDefault();
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const lineContent = value.slice(
        lineStart + BULLET.length,
        selectionStart
      );

      // Empty bullet item — stay in place
      if (lineContent.trim() === '' && selectionStart === selectionEnd) return;

      const next =
        value.slice(0, selectionStart) +
        '\n' +
        BULLET +
        value.slice(selectionEnd);
      onChange(next);

      const newCursor = selectionStart + 1 + BULLET.length;
      requestAnimationFrame(() => {
        ref.current?.setSelectionRange(newCursor, newCursor);
      });
      return;
    }

    if (e.key === 'Backspace' && selectionStart === selectionEnd) {
      const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
      const posInLine = selectionStart - lineStart;

      if (posInLine <= BULLET.length && posInLine > 0) {
        e.preventDefault();
        if (lineStart > 0) {
          // Remove line and jump cursor to end of previous line
          const prevLineEnd = lineStart - 1;
          const next =
            value.slice(0, prevLineEnd) +
            value.slice(lineStart + BULLET.length);
          onChange(next);
          requestAnimationFrame(() => {
            ref.current?.setSelectionRange(prevLineEnd, prevLineEnd);
          });
        } else if (value === BULLET) {
          // First (only) line is an empty bullet — clear the field
          onChange('');
        }
      }
    }
  }

  return (
    <TextArea
      ref={ref}
      label={label}
      placeholder={placeholder}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      className="[field-sizing:content]"
    />
  );
}
